import React from "react";
import { Box, Table, TableHead, TableRow, TableCell, TableBody, Button, Typography, CircularProgress, IconButton, TextField, Autocomplete, useTheme, useMediaQuery, Card, CardContent, CardActions, Divider, Container, Paper, LinearProgress } from "@mui/material";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import InfoComponent from "../customComponents/InfoComponent";
import { useSelector } from "react-redux";
import { selectInfo } from "../stateManagement/infoSlice";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoToggleButton from "../dashboard/menu/InfoToggleButton";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';
import DriveFileMoveRtlIcon from '@mui/icons-material/DriveFileMoveRtl';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';

export type GridColumnType<TOutput> = {
    [K in keyof TOutput]: {
        type?: 'Id' | 'Integer' | 'Decimal' | 'Text' | 'Price' | 'Phone' | 'List' | 'Date' | 'DateTime' | 'Time' | 'Enum' | 'Element';
        dontShow?: boolean;
        header?: string;
        searchable?: boolean;
        searchableOnTop?: boolean;
        showInCreateRow?: boolean;
        required?: boolean;
        min?: number;
        max?: number;
        enumObject?: any;
        minWidth?: number;
        isMoreColumn?: boolean;
    };
};

export interface PaginationType {
    skipCount: number;
    maxResultCount: number;
    totalCount: number;
}

export const defaultPaginationData: PaginationType = {
    maxResultCount: 5,
    skipCount: 0,
    totalCount: 0,
}

interface GridTableProps<TOutput, TFilterDto, TInput> {
    list: TOutput[];
    inputType: GridColumnType<TOutput>;
    onEditRow?: (input: TOutput) => void;
    onClickNew?: () => void;
    onClickDeleteFolder?: () => void;
    showDeleted?: boolean;
    onDeleteRow?: (input: TOutput) => void;
    deleteLoading?: any[];
    rowLoading?: any[];
    onClickRow?: (input: TOutput) => void;
    onClickRowInfo?: string;
    activateSelectableByField?: string;
    moreRowTools?: { buttonTitle?: string; icon?: React.ReactElement; onClick: (input: TOutput) => void; deactivateByField?: string; activateByField?: string; info?: string; }[]
    gridTitle?: string;
    gridTitleInfo?: string;
    showInfoToggleButton?: boolean;
    loading?: boolean;
    isOnDialog?: boolean;
    onClickBack?: () => void;
    onClickBackInfo?: string;
    moreTopTools?: { buttonTitle?: string; icon?: React.ReactElement; onClick: () => void; info?: string; }[]
    paginationData?: PaginationType;
    setPaginationData?: (input: PaginationType) => void;
    filterDto?: TFilterDto;
    setFilterDto?: (input: TFilterDto) => void;
    createRowDto?: TInput;
    setCreateRowDto?: (input: TInput) => void;
    searchButtonInCreateRow?: () => void;
    handleCreateRow?: (input: TInput) => void;
    searchButtonTop?: () => void;
    initialJustShowSearch?: boolean;
    customSearch?: React.ReactNode;
}

const GridTable = <TOutput extends object, TFilterDto, TInput>({
    inputType,
    list,
    onEditRow,
    onClickNew,
    onClickDeleteFolder,
    showDeleted,
    onDeleteRow,
    deleteLoading,
    rowLoading,
    onClickRow,
    onClickRowInfo,
    activateSelectableByField,
    moreRowTools,
    gridTitle,
    gridTitleInfo,
    showInfoToggleButton,
    loading,
    isOnDialog,
    onClickBack,
    onClickBackInfo,
    moreTopTools,
    paginationData,
    setPaginationData,
    filterDto,
    setFilterDto,
    createRowDto,
    setCreateRowDto,
    searchButtonInCreateRow,
    handleCreateRow,
    searchButtonTop,
    initialJustShowSearch,
    customSearch,
}: GridTableProps<TOutput, TFilterDto, TInput>) => {

    const visibleKeys = Object.keys(inputType).filter(
        (key) => (!inputType[key as keyof TOutput].dontShow && inputType[key as keyof TOutput].type)
    ) as (keyof TOutput)[];

    function getEnumKeyByValue(enumObj: any, value: any): string | undefined {
        if (!enumObj) return undefined;
        return Object.keys(enumObj).find((k) => enumObj[k] === value);
    }

    const renderCellContent = (key: keyof TOutput, value: any): any => {
        const columnType = inputType[key]?.type;

        switch (columnType) {
            case 'Price':
            case 'Decimal':
                return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            case 'Integer':
                return Number(value).toLocaleString();
            case 'Date':
                return value ? new Date(value).toLocaleDateString() : '';
            case 'DateTime':
                return value ? new Date(value).toLocaleString() : '';
            case 'Time':
                return value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
            case 'Phone':
                return value ? String(value).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : '';
            case 'Enum':
                return value !== undefined && value !== null
                    ? getEnumKeyByValue(inputType[key]?.enumObject, value) ?? String(value)
                    : '';
            case 'Element':
                return <Box>{value}</Box>;
            default:
                return value !== undefined && value !== null ? String(value) : '';
        }
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const infoState = useSelector(selectInfo);

    const [showMoreMap, setShowMoreMap] = React.useState<{ [key: number]: boolean }>({});

    const [isCreatableInMobile, setIsCreatableInMobile] = React.useState<boolean>(false);

    const toggleShowMore = (index: number) => {
        setShowMoreMap(prev => ({ ...prev, [index]: !prev[index] }));
    };

    return (
        <Box sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
            {(gridTitle || onClickBack || moreTopTools) &&
                <Box display="flex" alignItems="center" sx={{ padding: '10px', backgroundColor: isOnDialog ? 'primary.main' : 'inherit', position: 'sticky', top: isOnDialog ? '0' : '61px', zIndex: 1100 }}>
                    <Box display="flex" alignItems="center">
                        {onClickBack &&
                            <InfoComponent infoContent={onClickBackInfo ? onClickBackInfo : 'Back'}>
                                <IconButton size="small" disabled={loading} onClick={onClickBack} sx={{ marginRight: '7px' }} >
                                    <KeyboardArrowLeftIcon />
                                </IconButton>
                            </InfoComponent>
                        }
                        {gridTitle &&
                            <InfoComponent infoContent={gridTitleInfo}>
                                <Typography variant="h6" >{gridTitle}</Typography>
                            </InfoComponent>
                        }
                        {onClickNew &&
                            <InfoComponent infoContent='New item' >
                                <IconButton size="small" disabled={loading} onClick={onClickNew} sx={{ marginLeft: '7px' }} >
                                    <AddIcon />
                                </IconButton>
                            </InfoComponent>
                        }
                        {onClickDeleteFolder &&
                            <InfoComponent infoContent={showDeleted ? "Back to active items" : "View deleted items"} >
                                <IconButton size="small" disabled={loading} onClick={onClickDeleteFolder} sx={{ marginLeft: '7px' }} >
                                    {showDeleted ? <DriveFileMoveRtlIcon /> : <FolderDeleteIcon />}
                                </IconButton>
                            </InfoComponent>
                        }

                        {moreTopTools?.map((tool, index) => {
                            return tool.icon ? (
                                <InfoComponent key={index} infoContent={tool.info}>
                                    <IconButton
                                        size="small"
                                        onClick={() => tool.onClick()}
                                        disabled={loading}
                                        sx={{ marginLeft: '7px' }}
                                    >
                                        {tool.icon}
                                    </IconButton>
                                </InfoComponent>
                            ) : (
                                <InfoComponent key={index} infoContent={tool.info}>
                                    <Button
                                        onClick={() => tool.onClick()}
                                        disabled={loading}
                                        sx={{ marginLeft: '7px' }}
                                        variant="contained"
                                        size="small"
                                    >
                                        {tool.buttonTitle ?? 'more tool'}
                                    </Button>
                                </InfoComponent>
                            );
                        })}
                    </Box>
                    <Box flexGrow={1} />
                    {showInfoToggleButton && <InfoToggleButton />}
                    <Divider />
                </Box>
            }
            {customSearch && customSearch}
            {loading && <LinearProgress color="primary" />}
            <Box sx={{ overflowX: 'auto', padding: '10px 14px' }}>
                {(initialJustShowSearch && !list.length) ?
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignContent: 'center',
                        minHeight: 'calc(100vh - 140px)',
                        padding: '20px 0'
                    }}>
                        <Container component="main" maxWidth="xs">
                            <Paper style={{ padding: '2rem' }} elevation={3}>
                                <Box >
                                    <InfoComponent infoContent={`Filter items`} isLeftHorizontal={true} >
                                        <Box display="flex" flexDirection="column" gap={2} p={1}>
                                            {visibleKeys.map((key) => {
                                                const show = (inputType[key] as { searchableOnTop?: boolean })?.searchableOnTop;
                                                if (!show) return null;

                                                return (
                                                    <Box key={String(key)}>
                                                        {(inputType[key]?.type === 'Enum' && inputType[key]?.enumObject) ? (
                                                            <Autocomplete
                                                                fullWidth
                                                                options={['', ...Object.values(inputType[key].enumObject).filter(v => typeof v === 'number')]}
                                                                getOptionLabel={(option) => {
                                                                    if (option === '') return '';
                                                                    const label = inputType[key].enumObject[option as number];
                                                                    return label ?? String(option);
                                                                }}
                                                                value={filterDto?.[key as unknown as keyof TFilterDto] as string | number | null ?? ''}
                                                                onChange={(_, newValue) =>
                                                                    setFilterDto?.({
                                                                        ...filterDto,
                                                                        [key]: newValue ?? ''
                                                                    } as TFilterDto)
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        variant="standard"
                                                                        label={inputType[key]?.header || String(key)}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                )}
                                                            />
                                                        ) : (
                                                            <TextField
                                                                fullWidth
                                                                variant="standard"
                                                                label={inputType[key]?.header || String(key)}
                                                                InputLabelProps={{ shrink: true }}
                                                                value={filterDto?.[key as unknown as keyof TFilterDto] ?? ''}
                                                                onChange={(e) =>
                                                                    setFilterDto?.({
                                                                        ...filterDto,
                                                                        [key]: e.target.value
                                                                    } as TFilterDto)
                                                                }
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </InfoComponent>
                                    {searchButtonTop && (
                                        <Box display="flex" flexDirection="row" justifyContent="flex-end" gap={2} p={1}>
                                            <InfoComponent infoContent={`Search in ${gridTitle}`} >
                                                <Button
                                                    variant="contained"
                                                    disabled={loading}
                                                    onClick={() => searchButtonTop()}
                                                    size="small"
                                                >
                                                    <SearchIcon />Search
                                                </Button>
                                            </InfoComponent>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </Container>
                    </Box>
                    : isMobile ? (
                        <Box>
                            {searchButtonTop &&
                                <Card variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
                                    <CardContent
                                        sx={{
                                            position: 'sticky',
                                            right: 0,
                                            backgroundColor: 'background.paper',
                                            zIndex: 1,
                                            minWidth: '120px',
                                            m: 0,
                                            p: 0,
                                            paddingBottom: '0 !important',
                                        }}>
                                        <InfoComponent infoContent={`Filter items`} isLeftHorizontal={true} >
                                            <Box display='flex' flexDirection='row' sx={{ overflowX: infoState ? 'hidden' : 'auto', padding: '15px' }}>

                                                {visibleKeys.map((key) => {
                                                    const show = (inputType[key] as { searchableOnTop?: boolean })?.searchableOnTop;
                                                    if (!show) return null;

                                                    return (

                                                        <Box key={String(key)} sx={{ minWidth: `${inputType[key]?.minWidth ?? 120}px`, marginLeft: '15px' }}>
                                                            {(inputType[key]?.type === 'Enum' && inputType[key]?.enumObject) ? (
                                                                <Autocomplete
                                                                    fullWidth
                                                                    options={['', ...Object.values(inputType[key].enumObject).filter(v => typeof v === 'number')]}
                                                                    getOptionLabel={(option) => {
                                                                        if (option === '') return '';
                                                                        const label = inputType[key].enumObject[option as number];
                                                                        return label ?? String(option);
                                                                    }}
                                                                    value={filterDto?.[key as unknown as keyof TFilterDto] as string | number | null ?? ''}
                                                                    onChange={(_, newValue) =>
                                                                        setFilterDto?.({
                                                                            ...filterDto,
                                                                            [key]: newValue ?? ''
                                                                        } as TFilterDto)
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            variant="standard"
                                                                            label={inputType[key]?.header || String(key)}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    )}
                                                                />
                                                            ) : (

                                                                <TextField
                                                                    fullWidth
                                                                    variant="standard"
                                                                    label={inputType[key]?.header || String(key)}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    value={filterDto?.[key as unknown as keyof TFilterDto] ?? ''}
                                                                    onChange={(e) =>
                                                                        setFilterDto?.({
                                                                            ...filterDto,
                                                                            [key]: e.target.value
                                                                        } as TFilterDto)
                                                                    }
                                                                />

                                                            )}
                                                        </Box>

                                                    );
                                                })}

                                            </Box>
                                        </InfoComponent>
                                        <Box display="flex" justifyContent="flex-end" margin='10px' >
                                            <InfoComponent infoContent={`Search in ${gridTitle}`} >
                                                <IconButton
                                                    size="small"
                                                    disabled={loading}
                                                    onClick={() => searchButtonTop()}
                                                >
                                                    <SearchIcon />
                                                </IconButton>
                                            </InfoComponent>
                                        </Box>
                                    </CardContent>
                                </Card>
                            }
                            <Box display="flex" flexDirection="column" gap={2} paddingTop='10px' >
                                {
                                    list.map((row, rowIndex) => {
                                        const baseKeys = visibleKeys.filter(key => !inputType[key]?.isMoreColumn && row[key]);
                                        const moreKeys = visibleKeys.filter(key => inputType[key]?.isMoreColumn && row[key]);
                                        return (
                                            <Card key={rowIndex} variant="outlined">
                                                <CardContent>
                                                    {baseKeys.map((key) => (
                                                        <Box key={String(key)} mb={1}>
                                                            <Typography variant="subtitle2">{inputType[key]?.header || String(key)}</Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {renderCellContent(key, row[key])}
                                                            </Typography>
                                                            <Divider sx={{ mt: 1 }} />
                                                        </Box>
                                                    ))}
                                                    {showMoreMap[rowIndex] && moreKeys.map((key) => (
                                                        <Box key={String(key)} mb={1}>
                                                            <Typography variant="subtitle2">{inputType[key]?.header || String(key)}</Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {renderCellContent(key, row[key])}
                                                            </Typography>
                                                            <Divider sx={{ mt: 1 }} />
                                                        </Box>
                                                    ))}
                                                </CardContent>
                                                <CardActions>
                                                    {onClickRow && (list[rowIndex][activateSelectableByField as keyof TOutput] ?? true) && !showDeleted &&
                                                        <InfoComponent infoContent={onClickRowInfo} >
                                                            <Button disabled={loading || rowLoading?.includes((row as any).id)} variant="contained" size="small" onClick={() => onClickRow(row)}>
                                                                Select
                                                            </Button>
                                                        </InfoComponent>
                                                    }
                                                    {onEditRow && (
                                                        <InfoComponent infoContent='Edit item'>
                                                            <IconButton disabled={loading || rowLoading?.includes((row as any).id)} size="small" onClick={() => onEditRow(row)}>
                                                                <EditIcon />
                                                            </IconButton>
                                                        </InfoComponent>
                                                    )}
                                                    {onDeleteRow && (
                                                        <InfoComponent infoContent={showDeleted ? 'Restore item' : 'Delete item'}  >
                                                            <IconButton size="small" onClick={() => onDeleteRow(row)} disabled={deleteLoading?.includes((row as any).id) || rowLoading?.includes((row as any).id)}>
                                                                {showDeleted ? <RestoreFromTrashIcon /> : <DeleteIcon />}
                                                            </IconButton>
                                                        </InfoComponent>
                                                    )}
                                                    {moreRowTools?.map((tool, index) => {
                                                        const isDeactivated = tool.deactivateByField ? Boolean(list[rowIndex][tool.deactivateByField as keyof TOutput]) : false;
                                                        const isActivated = tool.activateByField ? Boolean(list[rowIndex][tool.activateByField as keyof TOutput]) : true;


                                                        if (!isActivated || isDeactivated) return null;

                                                        return tool.icon ? (
                                                            <InfoComponent key={index} infoContent={tool.info} >
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => tool.onClick(row)}
                                                                    disabled={loading || rowLoading?.includes((row as any).id)}
                                                                    sx={{ marginRight: '7px' }}
                                                                >
                                                                    {tool.icon}
                                                                </IconButton>
                                                            </InfoComponent>
                                                        ) : (
                                                            <InfoComponent key={index} infoContent={tool.info} >
                                                                <Button
                                                                    onClick={() => tool.onClick(row)}
                                                                    disabled={loading || rowLoading?.includes((row as any).id)}
                                                                    sx={{ marginRight: '7px' }}
                                                                    variant="contained"
                                                                    size="small"
                                                                >
                                                                    {tool.buttonTitle ?? 'more tool'}
                                                                </Button>
                                                            </InfoComponent>
                                                        );
                                                    })}
                                                    {rowLoading?.includes((row as any).id) && (
                                                        <CircularProgress size={20} sx={{ ml: 1 }} />
                                                    )}
                                                    {moreKeys.length > 0 && (
                                                        <InfoComponent infoContent={showMoreMap[rowIndex] ? 'Less data' : 'More data'} >
                                                            <IconButton disabled={loading || rowLoading?.includes((row as any).id)} size="small" onClick={() => toggleShowMore(rowIndex)}>
                                                                {showMoreMap[rowIndex] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                            </IconButton>
                                                        </InfoComponent>
                                                    )}
                                                </CardActions>
                                            </Card>
                                        );
                                    })}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: '100%' }}>
                                <TableHead>
                                    {!(filterDto && setFilterDto && searchButtonTop) && !(handleCreateRow && createRowDto && setCreateRowDto) && <TableRow>
                                        {visibleKeys.map((key) => (
                                            <TableCell key={String(key)} sx={{ minWidth: `${inputType[key]?.minWidth ?? 100}px` }}>
                                                {inputType[key]?.header || String(key)}
                                            </TableCell>
                                        ))}
                                        {(moreRowTools || onDeleteRow || onEditRow || searchButtonTop) &&
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    right: 0,
                                                    backgroundColor: 'background.paper',
                                                    zIndex: 2,
                                                    minWidth: '120px'
                                                }}
                                            >
                                                Tools
                                            </TableCell>}
                                    </TableRow>}

                                    {/* 🔍 Search Row */}
                                    {filterDto && setFilterDto && searchButtonTop && (
                                        <TableRow>
                                            {visibleKeys.map((key) => (
                                                <TableCell key={String(key)}>
                                                    <InfoComponent infoContent={`filter with ${inputType[key]?.header || String(key)}`} isLeftHorizontal={true} >
                                                        {(inputType[key] as { searchableOnTop?: boolean })?.searchableOnTop ? (
                                                            inputType[key]?.type === 'Enum' && inputType[key]?.enumObject ? (
                                                                <Autocomplete
                                                                    fullWidth
                                                                    options={['', ...Object.values(inputType[key].enumObject).filter(v => typeof v === 'number')]}
                                                                    getOptionLabel={(option) => {
                                                                        if (option === '') return '';
                                                                        const label = inputType[key].enumObject[option as number];
                                                                        return label ?? String(option);
                                                                    }}
                                                                    value={filterDto?.[key as unknown as keyof TFilterDto] as string | number | null ?? ''}
                                                                    onChange={(_, newValue) =>
                                                                        setFilterDto?.({
                                                                            ...filterDto,
                                                                            [key]: newValue ?? ''
                                                                        } as TFilterDto)
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            variant="standard"
                                                                            label={inputType[key]?.header || String(key)}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    )}
                                                                />
                                                            ) : (
                                                                <TextField
                                                                    fullWidth
                                                                    variant="standard"
                                                                    label={inputType[key]?.header || String(key)}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    value={filterDto?.[key as unknown as keyof TFilterDto] ?? ''}
                                                                    onChange={(e) =>
                                                                        setFilterDto?.({
                                                                            ...filterDto,
                                                                            [key]: e.target.value
                                                                        } as TFilterDto)
                                                                    }
                                                                />
                                                            )
                                                        ) : null}
                                                    </InfoComponent>
                                                </TableCell>
                                            ))}
                                            {(moreRowTools || onDeleteRow || onEditRow) && (
                                                <TableCell
                                                    sx={{
                                                        position: 'sticky',
                                                        right: 0,
                                                        backgroundColor: 'background.paper',
                                                        zIndex: 1,
                                                        minWidth: '120px',
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        minHeight: '48px',
                                                        alignItems: 'flex-end'
                                                    }}
                                                >
                                                    {searchButtonTop && (
                                                        <InfoComponent infoContent={`Search in ${gridTitle}`} >
                                                            <IconButton
                                                                size="small"
                                                                disabled={loading}
                                                                onClick={() => searchButtonTop()}
                                                                sx={{ marginLeft: '7px' }}
                                                            >
                                                                <SearchIcon />
                                                            </IconButton>
                                                        </InfoComponent>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    )}

                                    {/* ➕ Create Row */}
                                    {handleCreateRow && createRowDto && setCreateRowDto && (
                                        <TableRow>
                                            {visibleKeys.map((key) => (
                                                <TableCell
                                                    sx={{
                                                        position: 'sticky',
                                                        right: 0,
                                                        backgroundColor: 'background.paper',
                                                        zIndex: 1,
                                                        minWidth: '120px'
                                                    }}
                                                    key={String(key)}>
                                                    <InfoComponent infoContent={`${inputType[key]?.header || String(key)} (Input field in new form)`} isLeftHorizontal={true} >
                                                        {(inputType[key] as { showInCreateRow?: boolean })?.showInCreateRow ? (
                                                            inputType[key]?.type === 'Enum' && inputType[key]?.enumObject ? (
                                                                // --- Enum handling ---
                                                                <Autocomplete
                                                                    fullWidth
                                                                    options={['', ...Object.values(inputType[key].enumObject).filter(v => typeof v === 'number')]}
                                                                    getOptionLabel={(option) => {
                                                                        if (option === '') return '';
                                                                        const label = inputType[key].enumObject[option as number];
                                                                        return label ?? String(option);
                                                                    }}
                                                                    value={createRowDto?.[key as unknown as keyof TInput] as string | number | null ?? ''}
                                                                    onChange={(_, newValue) =>
                                                                        setCreateRowDto?.({
                                                                            ...createRowDto,
                                                                            [key]: newValue ?? ''
                                                                        } as TInput)
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            variant="standard"
                                                                            label={inputType[key]?.header || String(key)}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    )}
                                                                />
                                                            ) : inputType[key]?.type === 'Integer' || inputType[key]?.type === 'Decimal' ? (
                                                                // --- Number handling ---
                                                                <TextField
                                                                    type="number"
                                                                    fullWidth
                                                                    variant="standard"
                                                                    label={inputType[key]?.header || String(key)}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    value={createRowDto?.[key as unknown as keyof TInput] ?? ''}
                                                                    onChange={(e) =>
                                                                        setCreateRowDto?.({
                                                                            ...createRowDto,
                                                                            [key]: e.target.value === '' ? null : Number(e.target.value)
                                                                        } as TInput)
                                                                    }
                                                                />
                                                            ) : (
                                                                // --- Default text field ---
                                                                <TextField
                                                                    fullWidth
                                                                    variant="standard"
                                                                    label={inputType[key]?.header || String(key)}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    value={createRowDto?.[key as unknown as keyof TInput] ?? ''}
                                                                    onChange={(e) =>
                                                                        setCreateRowDto?.({
                                                                            ...createRowDto,
                                                                            [key]: e.target.value
                                                                        } as TInput)
                                                                    }
                                                                />
                                                            )
                                                        ) : null}
                                                    </InfoComponent>
                                                </TableCell>
                                            ))}

                                            {(moreRowTools || onDeleteRow || onEditRow || searchButtonTop) && (
                                                <TableCell
                                                    sx={{
                                                        position: 'sticky',
                                                        right: 0,
                                                        backgroundColor: 'background.paper',
                                                        zIndex: 1,
                                                        minWidth: '120px',
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        minHeight: '48px',
                                                        alignItems: 'flex-end'
                                                    }}
                                                >
                                                    {handleCreateRow && (
                                                        <InfoComponent infoContent={`Submit new form in list`}>
                                                            <IconButton
                                                                size="small"
                                                                disabled={loading}
                                                                onClick={() => handleCreateRow(createRowDto)}
                                                                sx={{ marginLeft: '7px' }}
                                                            >
                                                                <AddIcon />
                                                            </IconButton>
                                                        </InfoComponent>
                                                    )}
                                                    {searchButtonInCreateRow && (
                                                        <InfoComponent infoContent={`Search in ${gridTitle}`} >
                                                            <IconButton
                                                                size="small"
                                                                disabled={loading}
                                                                onClick={() => searchButtonInCreateRow()}
                                                                sx={{ marginLeft: '7px' }}
                                                            >
                                                                <SearchIcon />
                                                            </IconButton>
                                                        </InfoComponent>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    )}
                                </TableHead>
                                <TableBody>
                                    {list.map((row, rowIndex) => (
                                        <TableRow
                                            key={rowIndex}
                                            sx={{ cursor: (onClickRow && !loading && !infoState && (list[rowIndex][activateSelectableByField as keyof TOutput] ?? true) && !showDeleted) ? 'pointer' : 'auto' }}
                                        >
                                            {visibleKeys.map((key) => (
                                                <TableCell onClick={() => { if ((onClickRow && !loading && !infoState && (list[rowIndex][activateSelectableByField as keyof TOutput] ?? true) && !showDeleted)) onClickRow?.(row) }} key={String(key)}>
                                                    {renderCellContent(key, row[key])}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    right: 0,
                                                    backgroundColor: 'background.paper',
                                                    zIndex: 1,
                                                    minWidth: '120px',
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    minHeight: '21px',
                                                }}
                                            >
                                                {moreRowTools?.map((tool, index) => {
                                                    const isDeactivated = tool.deactivateByField ? Boolean(list[rowIndex][tool.deactivateByField as keyof TOutput]) : false;
                                                    const isActivated = tool.activateByField ? Boolean(list[rowIndex][tool.activateByField as keyof TOutput]) : true;

                                                    if (!isActivated || isDeactivated) return null;

                                                    return tool.icon ? (
                                                        <InfoComponent key={index} infoContent={tool.info} >
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => tool.onClick(row)}
                                                                disabled={loading || rowLoading?.includes((row as any).id)}
                                                                sx={{ marginRight: '7px' }}
                                                            >
                                                                {tool.icon}
                                                            </IconButton>
                                                        </InfoComponent>
                                                    ) : (
                                                        <InfoComponent key={index} infoContent={tool.info}>
                                                            <Button
                                                                onClick={() => tool.onClick(row)}
                                                                disabled={loading || rowLoading?.includes((row as any).id)}
                                                                sx={{ marginRight: '7px' }}
                                                                variant="contained"
                                                                size="small"
                                                            >
                                                                {tool.buttonTitle ?? 'more tool'}
                                                            </Button>
                                                        </InfoComponent>
                                                    );
                                                })}
                                                {onEditRow &&
                                                    <InfoComponent infoContent='Edit item'>
                                                        <IconButton size="small" disabled={loading || rowLoading?.includes((row as any).id)} onClick={() => onEditRow(row)} sx={{ marginRight: '7px' }} >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </InfoComponent>
                                                }
                                                {onDeleteRow &&
                                                    <>
                                                        <InfoComponent infoContent={showDeleted ? 'Restore item' : 'Delete item'}  >
                                                            <IconButton size="small" disabled={loading || rowLoading?.includes((row as any).id)} onClick={() => onDeleteRow(row)} sx={{ marginRight: '7px' }} >
                                                                {showDeleted ? <RestoreFromTrashIcon /> : <DeleteIcon />}
                                                            </IconButton>
                                                        </InfoComponent>
                                                        {'id' in row && typeof (row as any)['id'] === 'number' && deleteLoading?.includes((row as any)['id']) && (
                                                            <CircularProgress size={20} sx={{ ml: 1 }} />
                                                        )}

                                                    </>
                                                }
                                                {rowLoading?.includes((row as any).id) && (
                                                    <CircularProgress size={20} sx={{ ml: 1 }} />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                {/* {paginationData && setPaginationData && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                        <InfoComponent infoContent='Go to previous page'  >
                            <IconButton
                                disabled={paginationData.skipCount === 0 || loading}
                                onClick={() =>
                                    setPaginationData({
                                        ...paginationData,
                                        skipCount: Math.max(0, paginationData.skipCount - paginationData.maxResultCount),
                                    })
                                }
                            >
                                <NavigateBeforeIcon />
                            </IconButton>
                        </InfoComponent>
                        <InfoComponent infoContent='Pagination'  >
                            <Typography>
                                Page {Math.floor(paginationData.skipCount / paginationData.maxResultCount) + 1} of{' '}
                                {Math.ceil(paginationData.totalCount / paginationData.maxResultCount)}
                            </Typography>
                        </InfoComponent>
                        <InfoComponent infoContent='Go to next page'  >
                            <IconButton
                                disabled={
                                    paginationData.skipCount + paginationData.maxResultCount >= paginationData.totalCount || loading
                                }
                                onClick={() =>
                                    setPaginationData({
                                        ...paginationData,
                                        skipCount: paginationData.skipCount + paginationData.maxResultCount,
                                    })
                                }
                            >
                                <NavigateNextIcon />
                            </IconButton>
                        </InfoComponent>
                    </Box>
                )} */}
            </Box>
        </Box>
    );
};
export default GridTable;
